/**
 * Service API de la modération de la messagerie (docs/MODERATION_MESSAGERIE.md).
 * Les files à traiter se lisent par la file unique (`features/moderation`) ;
 * ce service garde les actions, les listes de suivi et les réglages.
 */

import { ADMIN_URLS } from '../config/adminApi';
import { createApiClient, withEnv } from '../lib/http';
import { AppEnvironment } from '../config/environment';

const api = createApiClient();
const BASE = ADMIN_URLS.moderationMessagerie;

export interface Suspension {
  uid: string;
  motif: string;
  par: string;
  incidentId?: string | null;
  suspenduLe: string;
}

export interface MessageFil {
  id: string;
  senderId: string;
  senderType: string;
  senderName?: string;
  content: string;
  messageType: string;
  imageUrl?: string | null;
  fileUrl?: string | null;
  createdAt: string;
}

export type NiveauSafeSearch = 'VERY_UNLIKELY' | 'UNLIKELY' | 'POSSIBLE' | 'LIKELY' | 'VERY_LIKELY';
export type Seuils = Partial<Record<'adult' | 'racy' | 'violence' | 'medical' | 'spoof', NiveauSafeSearch>>;

/** Mise en service : désactivée (comme avant), observation (analyse sans bloquer), active. */
export type ModeModeration = 'desactivee' | 'observation' | 'active';

export interface ReglagesModeration {
  mode: ModeModeration;
  image: { refus: Seuils; retenue: Seuils; retenueAvecEleve: Seuils };
  pdfPagesMax: number;
  delaiImageS: number;
  delaiTexteS: number;
  analyseTexte: boolean;
  accepterUrlsClient: boolean;
  /** Texte : filtre local puis Google (défaut) ou ancien modèle (llm). */
  moteurTexte: MoteurTexte;
  /** Tout message d'un adulte vers un élève passe aussi par Google. */
  googleAdulteVersEleve: boolean;
  /** Confiance Google (0–1) à partir de laquelle une catégorie est retenue. */
  seuilsGoogle: Record<string, number>;
  /** Détresse d'un élève : numéro d'aide de la carte ('' = pas de numéro proposé). */
  numeroAide: string;
  /** Détresse d'un élève : texte de la carte montrée sous son message. */
  texteAide: string;
}

export type MoteurTexte = 'local_google' | 'llm';

/** Blocage posé par un utilisateur sur son interlocuteur (P0-6). */
export interface BlocageMessagerie {
  id: string;
  par: string;
  cible: string;
  conversationId: string;
  creeLe: string;
}

/** Décision qu'aurait prise la modération sur un message livré (mode observation). */
export interface ObservationModeration {
  id: string;
  messageId: string;
  conversationId: string;
  typeFil: string;
  expediteurUid: string;
  roleExpediteur: string;
  typeMessage: string;
  avecPiece: boolean;
  decision: 'livre' | 'retenu' | 'refuse';
  motifs: string[];
  suspensionPrevue: boolean;
  texteNonAnalyse: boolean;
  dureeMs: number;
  creeLe: string;
}

export interface BilanObservations {
  total: number;
  livres: number;
  retenus: number;
  refuses: number;
  suspensionsPrevues: number;
  textesNonAnalyses: number;
  dureeMoyenneMs: number;
  dureeLenteMs: number;
}

/** Résultat d'une analyse de test (diagnostic de mise en service). */
export interface EssaiAnalyse {
  ok: boolean;
  dureeMs: number;
  resultat?: unknown;
  erreur?: string;
}

export interface DiagnosticModeration {
  image: EssaiAnalyse;
  texte: EssaiAnalyse;
  pret: boolean;
  mode: ModeModeration;
}

class ModerationMessagerieApiService {
  static async valider(env: AppEnvironment, id: string): Promise<void> {
    await api.post(`${BASE}/file/${id}/valider`, {}, withEnv(env));
  }

  static async supprimer(env: AppEnvironment, id: string): Promise<void> {
    await api.post(`${BASE}/file/${id}/supprimer`, {}, withEnv(env));
  }

  static async cloreIncident(env: AppEnvironment, id: string, note: string): Promise<void> {
    await api.post(`${BASE}/incidents/${id}/clore`, { note }, withEnv(env));
  }

  static async suspensions(env: AppEnvironment): Promise<Suspension[]> {
    const r = await api.get<{ data: Suspension[] }>(`${BASE}/suspensions`, withEnv(env));
    return r.data.data;
  }

  static async suspendre(env: AppEnvironment, uid: string, motif: string): Promise<void> {
    await api.post(`${BASE}/suspensions/${encodeURIComponent(uid)}`, { motif }, withEnv(env));
  }

  static async leverSuspension(env: AppEnvironment, uid: string): Promise<void> {
    await api.delete(`${BASE}/suspensions/${encodeURIComponent(uid)}`, withEnv(env));
  }

  /** Lecture d'un fil : journalisée côté serveur, motif obligatoire. */
  static async fil(env: AppEnvironment, conversationId: string, motif: string): Promise<MessageFil[]> {
    const r = await api.get<{ data: { messages: MessageFil[] } }>(
      `${BASE}/conversations/${conversationId}`, withEnv(env, { params: { motif } }));
    return r.data.data.messages;
  }

  static async reglages(env: AppEnvironment): Promise<ReglagesModeration> {
    const r = await api.get<{ data: ReglagesModeration }>(`${BASE}/reglages`, withEnv(env));
    return r.data.data;
  }

  static async enregistrerReglages(env: AppEnvironment, reglages: ReglagesModeration): Promise<ReglagesModeration> {
    const r = await api.put<{ data: ReglagesModeration }>(`${BASE}/reglages`, reglages, withEnv(env));
    return r.data.data;
  }

  /** Mode observation : décisions notées et leur bilan. */
  static async observations(env: AppEnvironment, limit = 100):
    Promise<{ observations: ObservationModeration[]; bilan: BilanObservations }> {
    const r = await api.get<{ data: { observations: ObservationModeration[]; bilan: BilanObservations } }>(
      `${BASE}/observations`, withEnv(env, { params: { limit } }));
    return r.data.data;
  }

  /** Teste les vraies analyses (image et texte de test) avant la mise en service. */
  static async diagnostic(env: AppEnvironment): Promise<DiagnosticModeration> {
    const r = await api.post<{ data: DiagnosticModeration }>(`${BASE}/diagnostic`, {}, withEnv(env));
    return r.data.data;
  }

  /** Blocages entre utilisateurs, les plus récents d'abord. */
  static async blocages(env: AppEnvironment, limit = 100): Promise<BlocageMessagerie[]> {
    const r = await api.get<{ data: BlocageMessagerie[] }>(`${BASE}/blocages`, withEnv(env, { params: { limit } }));
    return r.data.data;
  }

  /** Détresse d'un élève : entrées les plus récentes d'abord. */
  static async traiterDetresse(env: AppEnvironment, id: string): Promise<void> {
    await api.post(`${BASE}/detresse/${encodeURIComponent(id)}/traiter`, {}, withEnv(env));
  }

  static async traiterDifferee(env: AppEnvironment): Promise<{ traites: number; signales: number; echecs: number }> {
    const r = await api.post<{ data: { traites: number; signales: number; echecs: number } }>(
      `${BASE}/differee/traiter`, {}, withEnv(env));
    return r.data.data;
  }
}

export default ModerationMessagerieApiService;
